import { ReactNode, SyntheticEvent, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

// ----------------------------------------------------------------------
//
// Makes every clinical form inside it read-only while `locked`, without each
// form having to thread a `disabled` prop through RHF, Formik, Controller and
// raw MUI inputs alike:
//   • text inputs/textareas get `readOnly` (text stays selectable and copyable),
//   • selects, autocompletes, checkboxes, switches, radios, pickers, chips and
//     drawing canvases ignore pointer and keyboard input,
//   • submit buttons and anything marked `data-lock-hide` (Reset, Clear,
//     "Add new…", copy-from-previous) are hidden.
//   • dialogs opened from a locked form are portals outside this area, so the
//     buttons that open an edit dialog must carry `data-lock-hide` themselves.
// Navigation (tabs, Next/Previous) keeps working so the record can be read.

const CONTROL_SELECTOR = [
  '.MuiSelect-select',
  '.MuiAutocomplete-root',
  '.MuiCheckbox-root',
  '.MuiSwitch-root',
  '.MuiRadio-root',
  '.MuiFormControlLabel-root',
  '.MuiSlider-root',
  '.MuiChip-root',
  '.MuiInputAdornment-root',
  '.MuiToggleButton-root',
  'canvas',
  'input',
  'textarea',
  '[contenteditable="true"]',
  '[data-lock-block]',
].join(',');

const LOCK_MARK = 'data-clinical-lock-readonly';
const ALLOWED_KEYS = new Set([
  'Tab',
  'Shift',
  'Control',
  'Meta',
  'Alt',
  'Escape',
]);

type Props = {
  locked: boolean;
  children: ReactNode;
};

export default function ClinicalLockArea({ locked, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const apply = () => {
      root
        .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
          'input, textarea',
        )
        .forEach((el) => {
          if (locked) {
            if (!el.readOnly) {
              el.readOnly = true;
              el.setAttribute(LOCK_MARK, '');
            }
          } else if (el.hasAttribute(LOCK_MARK)) {
            el.readOnly = false;
            el.removeAttribute(LOCK_MARK);
          }
        });
    };

    apply();
    if (!locked) return undefined;
    // Sub-tabs and dialogs inside a form render their inputs later.
    const observer = new MutationObserver(apply);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [locked]);

  const block = (e: SyntheticEvent) => {
    if (!locked) return;
    const target = e.target as HTMLElement;
    // React events bubble out of portals (dialogs, menus). Only block controls
    // physically inside this area; dialogs gate themselves (e.g. "Add Clinical
    // Record" must keep working while a signed record is on screen).
    if (!ref.current?.contains(target)) return;
    if (target.closest(CONTROL_SELECTOR)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const blockKeys = (e: React.KeyboardEvent) => {
    if (!locked || ALLOWED_KEYS.has(e.key)) return;
    // Allow copy / select-all shortcuts on read-only text.
    if ((e.metaKey || e.ctrlKey) && ['c', 'a'].includes(e.key.toLowerCase()))
      return;
    block(e);
  };

  return (
    <Box
      ref={ref}
      data-clinical-locked={locked ? 'true' : undefined}
      onMouseDownCapture={(e) => {
        // Let text inputs receive focus/selection; block opening pickers/menus.
        const t = e.target as HTMLElement;
        if (
          locked &&
          (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') &&
          !t.closest('.MuiAutocomplete-root')
        ) {
          return;
        }
        block(e);
      }}
      onClickCapture={(e) => {
        const t = e.target as HTMLElement;
        if (
          locked &&
          (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') &&
          (t as HTMLInputElement).type !== 'checkbox' &&
          (t as HTMLInputElement).type !== 'radio' &&
          (t as HTMLInputElement).type !== 'file'
        ) {
          return;
        }
        block(e);
      }}
      onPointerDownCapture={(e) => {
        if (locked && (e.target as HTMLElement).tagName === 'CANVAS') block(e);
      }}
      onKeyDownCapture={blockKeys}
      onPasteCapture={block}
      onBeforeInputCapture={block}
      onDropCapture={block}
      sx={
        locked
          ? {
              '& button[type="submit"], & [data-lock-hide]': {
                display: 'none !important',
              },
              '& .MuiInputBase-root': { bgcolor: 'action.hover' },
              '& canvas': { cursor: 'not-allowed' },
            }
          : undefined
      }
    >
      {children}
    </Box>
  );
}
